import { CONTRACT_STATUS_LABELS, DIRECTION_LABELS } from "./contracts";
import { AI_PROVIDER_LABEL, DRAFT_ERROR_CODE_LABEL, DRAFT_STATUS_LABEL } from "./drafts";
import { EVENT_KIND_LABELS } from "./events";
import { FILE_TYPE_LABELS } from "./files";
import { CLAIM_RESULT_LABELS } from "./legal";
import {
  DAY_TYPE_LABELS as OBLIGATION_DAY_TYPE_LABELS,
  DEBTOR_LABELS,
  OBLIGATION_TYPE_LABELS,
  RULE_KIND_LABELS,
} from "./obligations";
import { DAY_TYPE_LABELS as CALENDAR_DAY_TYPE_LABELS } from "./references";
import { ROLE_LABELS, type Role } from "./roles";

/**
 * Клиент журнала аудита (E2, экран «Журнал аудита»). Доступен только юристу и админу — сервер
 * проверяет право `audit:view` на каждом маршруте; скрытие раздела в навигации это лишь дублирует.
 * Только чтение: журнал append-only (ТЗ 4.1.2), маршрутов изменения нет. Пути относительные
 * (`/api/...`), сессия — cookie HttpOnly (Р-5).
 */

/** Вид изменения (совпадает с enum сервера). */
export type AuditAction = "create" | "update" | "delete";

/** Автор действия. `null` — системное/фоновое действие (cron, миграция). */
export interface AuditActor {
  readonly id: string;
  readonly login: string;
  readonly fullName: string;
}

/** Строка журнала — ответ GET /api/audit (кто, когда, объект, действие, было/стало). */
export interface AuditEntry {
  readonly id: string;
  /** Момент действия — ISO-строка с таймзоной (timestamptz). В пояс Омска переводит UI. */
  readonly at: string;
  readonly actor: AuditActor | null;
  readonly entity: string;
  readonly entityId: string;
  readonly action: AuditAction;
  readonly before: unknown;
  readonly after: unknown;
}

/** Страница журнала: строки + общее число подходящих записей (для пагинации). */
export interface AuditPage {
  readonly items: AuditEntry[];
  readonly total: number;
  /** Имена для ссылочных полей снимков (E17, 18.09.2026) — см. `AuditRefs`. */
  readonly refs: AuditRefs;
}

/**
 * ИМЕНА ВМЕСТО ИДЕНТИФИКАТОРОВ В «БЫЛО/СТАЛО» (замечание владельца 18.09.2026 по шагу 12
 * чек-листа `docs/17-razvyortyvanie-staging.md`, чек-лист Б11 «Приёмка Инициатора договора на
 * staging»).
 *
 * Словари приходят С СЕРВЕРА и ровно для тех значений, что встретились на этой странице журнала.
 * Собирать их здесь из справочников нельзя: отключённая учётка в справочник активных не попадёт,
 * а журнал обязан назвать того, кого назначали, — он для того и ведётся.
 */
export interface AuditRefs {
  readonly users: Readonly<Record<string, string>>;
  readonly counterparties: Readonly<Record<string, string>>;
}

const EMPTY_REFS: AuditRefs = { users: {}, counterparties: {} };

/**
 * Ссылается ли поле снимка на объект, у которого есть имя. ПРАВИЛО, А НЕ ПЕРЕЧЕНЬ — повторяет
 * `apps/api/src/audit/refs.ts`: поле на `ById` и `initiatorId` — пользователь, `counterpartyId` —
 * контрагент. Новое поле-ссылка накрывается само, без правки этого места.
 */
export function refKind(field: string): "user" | "counterparty" | null {
  if (field === "counterpartyId") return "counterparty";
  if (field === "initiatorId" || /ById$/.test(field)) return "user";
  return null;
}

/** Параметры фильтра журнала. Пустые поля не сужают выборку. */
export interface AuditFilter {
  readonly entity?: string;
  readonly entityId?: string;
  /** Автор: id пользователя либо «system» (записи без автора). */
  readonly actorId?: string;
  readonly action?: AuditAction;
  /** Период — календарные дни `ГГГГ-ММ-ДД` (пересчёт в пояс организации — на сервере). */
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly page: number;
  readonly limit: number;
}

/** Успех со значением либо типизированная причина отказа с готовым сообщением для UI. */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly reason: "unauthorized" | "forbidden" | "validation" | "network";
      readonly message: string;
    };

interface ErrorBody {
  error?: string;
  message?: string;
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as ErrorBody | null;
  const message = body?.message;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте параметры фильтра." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось загрузить журнал." };
}

/** Собирает query-строку из фильтра, опуская пустые поля. */
function toQuery(filter: AuditFilter): string {
  const params = new URLSearchParams();
  if (filter.entity) params.set("entity", filter.entity);
  if (filter.entityId) params.set("entityId", filter.entityId);
  if (filter.actorId) params.set("actorId", filter.actorId);
  if (filter.action) params.set("action", filter.action);
  if (filter.dateFrom) params.set("dateFrom", filter.dateFrom);
  if (filter.dateTo) params.set("dateTo", filter.dateTo);
  params.set("page", String(filter.page));
  params.set("limit", String(filter.limit));
  return params.toString();
}

/** Страница журнала по фильтру (GET /api/audit). */
export async function fetchAuditPage(filter: AuditFilter): Promise<ApiResult<AuditPage>> {
  let res: Response;
  try {
    res = await fetch(`/api/audit?${toQuery(filter)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as AuditPage | null;
  return {
    ok: true,
    value: {
      items: body?.items ?? [],
      total: body?.total ?? 0,
      // Словаря может не быть у старого ответа — тогда ссылочные поля покажут «имя не найдено»,
      // а не сломают экран.
      refs: body?.refs ?? EMPTY_REFS,
    },
  };
}

/** Авторы для фильтра «по пользователю» (GET /api/audit/actors). */
export async function fetchAuditActors(): Promise<ApiResult<AuditActor[]>> {
  let res: Response;
  try {
    res = await fetch("/api/audit/actors", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { actors?: AuditActor[] } | null;
  return { ok: true, value: body?.actors ?? [] };
}

// ── Читаемое представление «было/стало» ──────────────────────────────────────

/** Типы бизнес-объектов, встречающиеся в журнале, — с русскими названиями (термины ТЗ). */
export const ENTITY_LABELS: Record<string, string> = {
  user: "Пользователь",
  contract: "Договор",
  counterparty: "Контрагент",
  specification: "Спецификация",
  obligation: "Обязательство",
  file: "Файл",
  // Код сущности события — `exec_event`: ровно так его пишет сервер (apps/api/src/events/store.ts).
  // Значение сверяется автотестом с серверным: разойдутся — фильтр «Событие исполнения» перестанет
  // что-либо находить, а в журнале появится латиница вместо русского названия (правило 8).
  exec_event: "Событие исполнения",
  claim: "Претензия",
  // D-53 (решение владельца 13.09.2026 — общая подпись): под этим кодом сервер пишет И пороги
  // уведомлений (E10), И реквизиты организации (E15). Прежняя подпись «Настройки уведомлений»
  // выдавала правку ИНН за правку порогов. Коды на сервере НЕ разводятся: общая подпись верна
  // сразу для всех строк, включая уже записанные, а переписать старые строки журнала нельзя
  // (запрет правки журналов, E2b). Что именно поменялось — читается по полям «Было / стало».
  org_settings: "Настройки организации",
  // D-49: три кода, которые сервер писал в журнал, а словарь не знал — журнал показывал их
  // латиницей. Названия — те же, что на экранах: «черновик» разбора (термин глоссария
  // «Черновик разбора»), разделы справочников в «Настройках». Полноту словаря теперь держит
  // сторож audit-entities.test.ts — он собирает коды из серверного кода по факту.
  draft: "Черновик разбора",
  key_rate: "Ключевая ставка ЦБ",
  calendar_day: "Производственный календарь",
};

/** Русское название типа объекта (или сам код, если незнаком). */
export function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity] ?? entity;
}

/** Русские наименования действий. */
export const ACTION_LABELS: Record<AuditAction, string> = {
  create: "Создание",
  update: "Изменение",
  delete: "Удаление",
};

/** Русские подписи полей снимка (по всем сущностям, встречающимся в аудите). */
const FIELD_LABELS: Record<string, string> = {
  login: "Логин",
  fullName: "ФИО",
  roles: "Роли",
  isActive: "Активность",
  hasPassword: "Пароль задан",
  passwordReset: "Сброс пароля",
  number: "Номер",
  date: "Дата",
  status: "Статус",
  subject: "Предмет",
  amount: "Сумма",
  name: "Наименование",
  inn: "ИНН",
  // Обязательства и события исполнения (E7/E8) — записи этих сущностей в журнале самые массовые.
  descr: "Описание",
  debtor: "Должник",
  type: "Тип",
  respRole: "Ответственная роль",
  manualStatus: "Ручной статус",
  doneDate: "Дата исполнения",
  obligationId: "Обязательство",
  kind: "Вид события",
  docRef: "Документ",
  note: "Примечание",
  isFull: "Исполнено полностью",
  // Настройки инстанса (E10, Приложение В ТЗ): пороги в календарных днях и переключатель В18.
  escalationDays: "Порог эскалации, дн.",
  notifyPayment: "Предупреждение о сроке оплаты, дн.",
  notifyDelivery: "Предупреждение о сроке поставки / приёмки, дн.",
  notifyOther: "Предупреждение по прочим типам, дн.",
  affiliatedMute: "Приглушать сигналы по аффилированным",
  // Реквизиты организации (E15) — те же слова, что на форме «Настройки → Организация»
  // (OrgProfileSection.tsx). Кроме `name` и `inn`: они подписаны выше, общие с контрагентом, и
  // `name` здесь «Наименование», а на форме — «Полное наименование» (D-53).
  kpp: "КПП",
  ogrn: "ОГРН",
  address: "Адрес",
  phone: "Телефон",
  email: "Электронная почта",
  seededFromEnv: "Перенесено из настроек сервера при запуске",
  // Черновик разбора (E11, D-24) — поля снимков из apps/api/src/drafts/store.ts (D-51).
  files: "Файлы",
  payload: "Поля черновика",
  provider: "Провайдер ИИ",
  model: "Модель ИИ",
  errorCode: "Код ошибки разбора",
  reason: "Причина",
  contractId: "Договор",
  rejectReason: "Причина отклонения",
  withdrawReason: "Причина отзыва",
  // Не «В работе у»: эту надпись сторож токенов разрешает только общему штампу InWorkStamp —
  // и правильно, здесь не штамп состояния, а подпись поля. Слова — как в причине записи сервера.
  inWorkById: "Взял в работу",
  // ── Остальные объекты журнала (D-51, дополнение 2 владельца 13.09.2026) ──
  // Слова — с форм и реестров, где поле показано; где подписи на экране нет — предложены по
  // терминам ТЗ и вынесены владельцу на сверку формулировок (отчёт по дополнению 2). Подписи
  // выше не трогаются: они приняты. Словарь общий для всех сущностей. Одно известное
  // расхождение смысла: `kind` у спецификации — вид документа, а подпись «Вид события» дана
  // событию исполнения и принята; вынесено владельцу в отчёте по дополнению 2, не правится.
  // Пользователь (экран «Пользователи», users/store.ts и auth/store.ts).
  mustChangePassword: "Ждёт смены пароля",
  passwordChangedBySelf: "Пароль сменён самим пользователем",
  // Договор — форма договора (ContractCard.tsx).
  counterpartyId: "Контрагент",
  direction: "Направление",
  validityTerm: "Срок действия",
  penPayRate: "Неустойка за просрочку оплаты, % в день",
  penDelRate: "Неустойка за просрочку поставки, % в день",
  penCap: "Потолок неустойки, ₽",
  penCapPercent: "Потолок неустойки, %",
  penCapBase: "База потолка неустойки",
  price: "Цена договора, ₽",
  claimRequired: "Обязателен досудебный (претензионный) порядок (ч. 5 ст. 4 АПК РФ)",
  claimDays: "Срок ответа на претензию, дней",
  court: "Подсудность (арбитражная оговорка)",
  // E17: Инициатор договора и номер по внутреннему Реестру заказчика. Переназначение Инициатора
  // пишется «было/стало» и читается в журнале как смена ответственного, а не как правка поля.
  initiatorId: "Инициатор",
  registryNumber: "Регистрационный номер по Реестру договоров",
  // Как появилась запись — сервер пишет словами («верификация черновика»).
  source: "Источник",
  // Контрагент — блок «Контактные лица» карточки договора и форма контрагента.
  contacts: "Контактные лица",
  affiliated: "Аффилированный (внутригрупповой)",
  // Обязательство — форма обязательства (ObligationsPanel.tsx) и правовая панель
  // (ObligationLegalPanel.tsx). Пометки формы «(необязательно)» в журнал не переносятся.
  specificationId: "Спецификация",
  ruleKind: "Вид правила",
  ruleDate: "Дата срока",
  ruleBase: "Отсчёт от даты",
  ruleDays: "Число дней",
  dayType: "Тип дней",
  triggerObligationId: "Обязательство-триггер",
  disputeCaseNo: "№ судебного дела",
  manualStatusReason: "Основание ручного статуса",
  manualStatusDate: "Дата ручного статуса",
  accrualDueDate: "Считать неустойку с даты",
  accrualDueReason: "Основание (мораторий, рассрочка, иное)",
  // Событие исполнения — сторнирование (E8a).
  reversesEventId: "Сторнирует событие",
  reversedByEventId: "Сторнировано событием",
  reversalReason: "Причина сторнирования",
  // Файл — панель файлов (FilesPanel.tsx). Размер сервер пишет в байтах, а не в КБ/МБ экрана.
  version: "Версия",
  originalName: "Имя файла",
  contentType: "Тип файла",
  size: "Размер, байт",
  // Претензия — форма претензии и ответа (ClaimsPanel.tsx).
  sentDate: "Дата направления",
  outNumber: "Исходящий №",
  answerDays: "Срок ответа, календарных дней",
  calcOn: "Расчёт на дату",
  result: "Результат",
  resultDate: "Дата ответа",
  resultNote: "Примечание",
  reversesClaimId: "Отменяет претензию",
  reversedBy: "Отменена претензией",
  // Ключевая ставка ЦБ — справочник (ReferenceSections.tsx). На форме «Ставка, % годовых», но
  // сервер пишет сотые доли процента (16 % → 1600) — подпись формы сказала бы неправду.
  from: "Действует с",
  rate: "Ставка, сотые доли % годовых",
  // Полноту по ВСЕМ сущностям, которые сервер пишет в журнал, держит сторож
  // audit-entities.test.ts: он собирает поля снимков из серверного кода по факту, и новое поле
  // без подписи роняет тест.
};

/** Русская подпись поля (или само имя, если незнакомо). */
export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/**
 * Словари ЗНАЧЕНИЙ: сущность → поле → код → русское слово (D-51, дополнение владельца 13.09.2026).
 *
 * По сущности, а не по полю: поле `status` есть и у договора, и у претензии, и у черновика, а
 * коды у них разные — подпись «Готов к верификации» на статусе претензии была бы ложью.
 * Словари не копируются: это те же подписи, что на экранах реестров и форм (lib/drafts.ts,
 * lib/contracts.ts, lib/obligations.ts, lib/events.ts, lib/legal.ts, lib/references.ts,
 * lib/roles.ts); у типа файла экранного словаря не было — он заведён рядом с файлами
 * (lib/files.ts). Неизвестный код показывается как есть — спрятать его значило бы скрыть от
 * администратора, что сервер записал что-то новое.
 *
 * Состав — ровно перечень владельца (дополнение 2 от 13.09.2026); сторож audit-entities.test.ts
 * держит, что каждое поле перечня здесь есть и что словарь знает каждый код сервера.
 */
const VALUE_LABELS: Record<string, Record<string, Record<string, string>>> = {
  draft: {
    status: DRAFT_STATUS_LABEL,
    provider: AI_PROVIDER_LABEL,
    errorCode: DRAFT_ERROR_CODE_LABEL,
  },
  contract: {
    status: CONTRACT_STATUS_LABELS,
    direction: DIRECTION_LABELS,
  },
  obligation: {
    type: OBLIGATION_TYPE_LABELS,
    debtor: DEBTOR_LABELS,
    respRole: ROLE_LABELS,
    ruleKind: RULE_KIND_LABELS,
    dayType: OBLIGATION_DAY_TYPE_LABELS,
  },
  exec_event: {
    kind: EVENT_KIND_LABELS,
  },
  claim: {
    result: CLAIM_RESULT_LABELS,
  },
  calendar_day: {
    type: CALENDAR_DAY_TYPE_LABELS,
  },
  file: {
    contentType: FILE_TYPE_LABELS,
  },
};

/** Контактное лицо одной строкой — как в блоке «Контактные лица» карточки договора. */
function contactLine(contact: unknown): string {
  if (contact === null || typeof contact !== "object") return String(contact);
  const { fio, position, phone, email } = contact as Record<string, unknown>;
  return [fio, position, phone, email]
    .filter((part) => typeof part === "string" && part !== "")
    .join(" · ");
}

/**
 * Человекочитаемое значение поля снимка: роли — по-русски, булевы — да/нет, массивы — через
 * запятую, коды — словами (если передана сущность), контактные лица — каждое своей строкой
 * (строки разделены переводом строки; экран показывает их одну под другой).
 */
export function formatFieldValue(
  field: string,
  value: unknown,
  entity?: string,
  refs: AuditRefs = EMPTY_REFS,
): string {
  if (value === null || value === undefined) return "—";
  /*
   * ССЫЛОЧНОЕ ПОЛЕ — ТОЛЬКО ИМЯ, идентификатор не показываем (18.09.2026, шаг 12 чек-листа Б11).
   * Имени нет — так и говорим: запись журнала остаётся честной, но UUID на экран не выносим —
   * он не сообщает читателю ничего, кроме того, что имя не нашлось.
   */
  const kind = refKind(field);
  if (kind !== null && typeof value === "string") {
    const slovar = kind === "user" ? refs.users : refs.counterparties;
    return slovar[value] ?? (kind === "user" ? "пользователь удалён" : "контрагент удалён");
  }
  const words = entity === undefined ? undefined : VALUE_LABELS[entity]?.[field];
  if (words !== undefined && typeof value === "string") {
    return Object.hasOwn(words, value) ? words[value]! : value;
  }
  if (field === "roles" && Array.isArray(value)) {
    return value.length > 0 ? value.map((r) => roleLabel(r)).join(", ") : "нет ролей";
  }
  if (field === "contacts" && Array.isArray(value)) {
    return value.length > 0 ? value.map(contactLine).join("\n") : "—";
  }
  if (typeof value === "boolean") return value ? "да" : "нет";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// Русские наименования ролей для значений поля roles (без импорта Record — узкая функция).
const ROLE_LABEL_MAP: Record<string, string> = {
  clerk: "Делопроизводитель",
  commercial: "Коммерческий отдел",
  accounting: "Бухгалтерия",
  lawyer: "Юрист",
  director: "Директор",
  admin: "Администратор",
};
function roleLabel(role: unknown): string {
  return typeof role === "string" ? (ROLE_LABEL_MAP[role] ?? role) : String(role);
}

/** Одно изменённое поле для diff-представления «было → стало». */
export interface FieldChange {
  readonly field: string;
  readonly label: string;
  readonly before: string | null;
  readonly after: string | null;
  /**
   * Свёрнутая строка вместо полного «было → стало» — у полей, значение которых целиком не
   * помещается в журнал (сейчас одно: «Поля черновика»). Есть — экран показывает её и кнопку
   * «Показать полностью»; нет — обычное «было → стало».
   */
  readonly summary?: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

/**
 * Названия частей разбора для свёрнутой строки «изменено: …» (D-51, дополнение владельца).
 *
 * Ключи — верхний уровень разбора (`ParsedContract`, apps/api/src/ai/schema.ts). Слова — с формы
 * верификации (pages/VerifyPage.tsx): заголовки разделов и подписи полей; сторож
 * audit-entities.test.ts держит, что каждое слово там действительно написано. Слово «Неустойка»
 * дано решением владельца 13.09.2026 (D-51, дополнение 2), а с E11b PR-5 оно стоит на форме
 * подзаголовком блока — исключений больше нет. Часть без подписи по-прежнему показывается своим
 * ключом.
 */
export const DRAFT_PAYLOAD_PART_LABELS: Record<string, string> = {
  contract: "Договор",
  parties: "Контрагент",
  direction: "Направление",
  penalties: "Неустойка",
  claimProcedure: "Досудебный (претензионный) порядок обязателен",
  specifications: "Спецификации",
  obligations: "Обязательства — предложение модели",
  notes: "Замечания модели",
};

/** Изменившиеся части разбора — русскими словами с формы, у незнакомой части — ключом. */
export function changedPayloadParts(before: unknown, after: unknown): string[] {
  const b = asRecord(before);
  const a = asRecord(after);
  return [...new Set([...Object.keys(b), ...Object.keys(a)])]
    .filter((part) => JSON.stringify(b[part]) !== JSON.stringify(a[part]))
    .map((part) => DRAFT_PAYLOAD_PART_LABELS[part] ?? part);
}

function isPlainObject(value: unknown): boolean {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Разница снимков «было»/«стало» — только изменившиеся поля (при create — все поля «стало»,
 * при delete — все поля «было»). Для read-only diff-подсветки на экране (Д-4 из docs/07).
 * Поля с равными значениями не показываем — журнал должен читаться как «что именно изменилось».
 * `entity` — код сущности записи: по нему значения кодов переводятся в слова.
 */
export function diffSnapshots(
  before: unknown,
  after: unknown,
  entity?: string,
  refs: AuditRefs = EMPTY_REFS,
): FieldChange[] {
  const b = asRecord(before);
  const a = asRecord(after);
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].sort();
  const changes: FieldChange[] = [];
  for (const field of keys) {
    const hadBefore = field in b;
    const hasAfter = field in a;
    const beforeStr = hadBefore ? formatFieldValue(field, b[field], entity, refs) : null;
    const afterStr = hasAfter ? formatFieldValue(field, a[field], entity, refs) : null;
    /*
     * Изменение: показываем, если отличается ПОКАЗ либо само значение.
     *
     * Второе условие добавлено 18.09.2026 вместе с именами ссылочных полей и закрывает дыру,
     * которую они сами и открыли: два РАЗНЫХ объекта, которых уже нет в базе, показываются
     * одинаково («контрагент удалён»), и сравнение по показу сочло бы их за «не менялось» —
     * строка исчезла бы из журнала целиком. Журнал не вправе умолчать об изменении потому, что
     * не сумел назвать его стороны.
     */
    if (beforeStr === afterStr && JSON.stringify(b[field]) === JSON.stringify(a[field])) continue;
    const change = { field, label: fieldLabel(field), before: beforeStr, after: afterStr };
    // «Поля черновика» — весь разбор договора одной строкой: без свёртки одна запись раздувает
    // таблицу журнала в ширину. Сворачиваем только объект: иное значение показываем как есть.
    const parts =
      entity === "draft" &&
      field === "payload" &&
      [b[field], a[field]].every((v) => v === undefined || v === null || isPlainObject(v))
        ? changedPayloadParts(b[field], a[field])
        : [];
    changes.push(
      parts.length > 0 ? { ...change, summary: `изменено: ${parts.join(", ")}` } : change,
    );
  }
  return changes;
}

/** Роль пользователя — реэкспорт типа для страниц, чтобы не тянуть roles.ts напрямую в UI аудита. */
export type { Role };
